import { 
  Factory, 
  Boxes, 
  Ship,
  QrCode,
  FileCheck,
  TrendingUp,
  Clock,
  Scale,
  Printer,
  Flame,
  Wheat
} from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

interface ScenarioStat {
  label: string;
  value: string;
  unit: string;
}

interface ScenarioFeature {
  icon: string;
  title: string;
  desc: string;
}

interface ScenarioItem {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  stats: ScenarioStat[];
  features: ScenarioFeature[];
  color: string;
  bgColor: string;
}

interface ScenariosContent {
  title: string;
  subtitle: string;
  description: string;
  scenarios: ScenarioItem[];
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Factory,
  Boxes,
  Ship,
  QrCode,
  FileCheck,
  TrendingUp,
  Clock,
  Scale,
  Printer,
  Flame,
  Wheat,
};

const defaultContent: ScenariosContent = {
  title: '多行业船运发货解决方案',
  subtitle: '应用场景',
  description: '深耕建材行业，为水泥、熟料、石子等产品提供专业的船运发货管理服务',
  scenarios: [
    {
      id: 'cement',
      icon: 'Factory',
      title: '水泥船运发货',
      subtitle: 'Cement Shipping',
      description: '针对水泥产品特性，提供从订单下达到船舶装货的全流程管理，支持散装水泥和袋装水泥的差异化管理。',
      stats: [
        { label: '日均发货量', value: '5000+', unit: '吨' },
        { label: '装货效率', value: '30%', unit: '提升' },
        { label: '等待时间', value: '40%', unit: '减少' },
      ],
      features: [
        { icon: 'QrCode', title: '扫码装货', desc: '一码通行，快速装货' },
        { icon: 'FileCheck', title: '自助计量', desc: '精准计量，实时上传' },
        { icon: 'TrendingUp', title: '效率提升', desc: '流程优化，效率倍增' },
        { icon: 'Clock', title: '实时监控', desc: '全程可视，实时掌控' },
      ],
      color: 'from-slate-500 to-slate-700',
      bgColor: 'bg-slate-50',
    },
    {
      id: 'aggregate',
      icon: 'Boxes',
      title: '砂石骨料船运',
      subtitle: 'Aggregate Shipping',
      description: '骨料船运发货管理，支持多种规格砂石骨料的分类管理和装船作业，满足不同客户的定制化需求。',
      stats: [
        { label: '日发货船次', value: '100+', unit: '船次' },
        { label: '品种管理', value: '20+', unit: '种' },
        { label: '客户满意度', value: '98%', unit: '' },
      ],
      features: [
        { icon: 'Boxes', title: '多品种管理', desc: '分类管理，清晰明了' },
        { icon: 'QrCode', title: '扫码识别', desc: '快速识别，准确无误' },
        { icon: 'TrendingUp', title: '数据分析', desc: '数据驱动，智能决策' },
        { icon: 'Clock', title: '快速结算', desc: '自助结算，实时到账' },
      ],
      color: 'from-stone-500 to-stone-700',
      bgColor: 'bg-stone-50',
    },
    {
      id: 'coal',
      icon: 'Flame',
      title: '煤炭船运发货',
      subtitle: 'Coal Shipping',
      description: '煤炭大宗物资船运管理，支持多港口调度和船舶配载优化，确保运输安全高效。',
      stats: [
        { label: '单船装载量', value: '5000+', unit: '吨' },
        { label: '调度效率', value: '50%', unit: '提升' },
        { label: '运输安全', value: '99.9%', unit: '' },
      ],
      features: [
        { icon: 'Ship', title: '大船调度', desc: '专业调度，高效配载' },
        { icon: 'FileCheck', title: '料斗秤计量', desc: '专业计量，准确可靠' },
        { icon: 'TrendingUp', title: '效率优化', desc: '智能调度，提升效率' },
        { icon: 'Clock', title: '全程跟踪', desc: '实时跟踪，安全到达' },
      ],
      color: 'from-amber-600 to-amber-800',
      bgColor: 'bg-amber-50',
    },
    {
      id: 'grain',
      icon: 'Wheat',
      title: '粮食船运发货',
      subtitle: 'Grain Shipping',
      description: '粮食作物船运管理，支持散粮和袋装粮食的分类运输，保障粮食运输过程中的品质和安全。',
      stats: [
        { label: '日吞吐量', value: '3000+', unit: '吨' },
        { label: '损耗降低', value: '80%', unit: '' },
        { label: '品质保障', value: '100%', unit: '' },
      ],
      features: [
        { icon: 'Boxes', title: '分类管理', desc: '品种分类，防混装' },
        { icon: 'QrCode', title: '溯源管理', desc: '全程溯源，保品质' },
        { icon: 'TrendingUp', title: '智能调度', desc: '优化配载，降成本' },
        { icon: 'Clock', title: '快速通关', desc: '自助审核，提效率' },
      ],
      color: 'from-emerald-500 to-emerald-700',
      bgColor: 'bg-emerald-50',
    },
  ],
};

export default function Scenarios() {
  const { content } = useSiteContent('scenarios');
  const data: ScenariosContent = content?.content || defaultContent;

  const getIcon = (iconName: string) => {
    return iconMap[iconName] || Factory;
  };

  return (
    <section id="scenarios" className="section-padding bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 mb-4">
            <span className="w-2 h-2 bg-ocean-500 rounded-full" />
            <span className="text-ocean-600 text-sm font-medium">{data.subtitle}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            {data.title}
          </h2>
          <p className="text-lg text-slate-600">
            {data.description}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {(data.scenarios || defaultContent.scenarios).map((scenario: ScenarioItem) => {
            const IconComponent = getIcon(scenario.icon);
            
            return (
              <div
                key={scenario.id}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-12 h-12 ${scenario.bgColor} rounded-xl flex items-center justify-center`}>
                    <IconComponent className="w-6 h-6 text-slate-700" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">{scenario.title}</h3>
                    <span className="text-sm text-slate-500">{scenario.subtitle}</span>
                  </div>
                </div>

                <p className="text-slate-600 mb-6 leading-relaxed">
                  {scenario.description}
                </p>

                <div className="grid grid-cols-3 gap-3 mb-6">
                  {scenario.stats.map((stat: ScenarioStat) => (
                    <div key={stat.label} className="text-center p-3 bg-slate-50 rounded-xl">
                      <div className="text-xl font-bold text-slate-900">{stat.value}</div>
                      <div className="text-xs text-slate-500">{stat.label}</div>
                      {stat.unit && <div className="text-xs text-slate-400">{stat.unit}</div>}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {scenario.features.map((feature: ScenarioFeature) => {
                    const FeatureIcon = getIcon(feature.icon);
                    return (
                      <div key={feature.title} className="flex items-start gap-2">
                        <div className="w-7 h-7 bg-ocean-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FeatureIcon className="w-3.5 h-3.5 text-ocean-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-800 text-sm">{feature.title}</div>
                          <div className="text-xs text-slate-500">{feature.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 bg-white rounded-2xl p-8 lg:p-12 shadow-lg">
          <h3 className="text-2xl font-bold text-slate-900 text-center mb-12">船运发货流程</h3>
          <div className="relative">
            <div className="hidden lg:block absolute top-16 left-0 right-0 h-0.5 bg-slate-200">
              <div className="absolute inset-0 bg-gradient-to-r from-ocean-400 via-primary-500 to-ocean-400" />
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-6">
              {[
                { step: '01', title: '订单下达', desc: '客户在线下单', icon: 'FileCheck' },
                { step: '02', title: '派船调度', desc: '智能分配船舶', icon: 'Ship' },
                { step: '03', title: '船舶预约', desc: '预约装船时间', icon: 'Ship' },
                { step: '04', title: '扫码装货', desc: '一码快速通行', icon: 'QrCode' },
                { step: '05', title: '自助计量', desc: '精准计量数据', icon: 'Scale' },
                { step: '06', title: '电子单据', desc: '自助打印单据', icon: 'Printer' },
              ].map((item) => {
                const StepIcon = getIcon(item.icon);
                return (
                  <div key={item.step} className="relative text-center">
                    <div className="relative z-10 w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-ocean-500 to-primary-600 rounded-full flex items-center justify-center shadow-lg shadow-ocean-500/30">
                      <span className="text-white font-bold">{item.step}</span>
                    </div>
                    
                    <div className="bg-slate-50 rounded-xl p-4">
                      <StepIcon className="w-5 h-5 text-ocean-600 mx-auto mb-2" />
                      <div className="font-semibold text-slate-800 text-sm mb-1">{item.title}</div>
                      <div className="text-xs text-slate-500">{item.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
